import { useState, useEffect } from 'react';
import { Activity, Layers, Cpu, Users, TrendingUp, Clock, Zap, Database } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { StatCard } from '../components/common/StatCard';
import { getNetworkStats, getTokenomics } from '../lib/api';
import { formatNumber, formatHashrate, getEpochProgress } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [tokenomics, setTokenomics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tokenRes] = await Promise.all([
          getNetworkStats(),
          getTokenomics(),
        ]);
        setStats(statsRes.data);
        setTokenomics(tokenRes.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const epochInfo = stats ? getEpochProgress(stats.current_height) : null;
  const supplyMined = stats ? ((stats.circulating_supply - 1200000) / 19800000) * 100 : 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="stats-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
            <Activity className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Network Statistics</h1>
            <p className="text-sm text-muted-foreground">
              Chain ID: {stats?.chain_id}
            </p>
          </div>
        </div>

        {/* Network Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Block Height"
            value={formatNumber(stats?.current_height, 0)}
            icon={Layers}
            accentColor="primary"
          />
          <StatCard
            title="Network Hashrate"
            value={formatHashrate(stats?.hashrate)}
            icon={Cpu}
            accentColor="secondary"
          />
          <StatCard
            title="Difficulty"
            value={formatNumber(stats?.difficulty, 0)}
            icon={Zap}
            accentColor="warning"
          />
          <StatCard
            title="Block Time"
            value={`${stats?.avg_block_time}s`}
            icon={Clock}
            accentColor="success"
          />
        </div>

        {/* Supply Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Circulating Supply"
            value={formatNumber(stats?.circulating_supply, 0)}
            subtitle="GG"
            icon={TrendingUp}
            accentColor="primary"
          />
          <StatCard
            title="Staked Supply"
            value={formatNumber(stats?.staked_supply, 0)}
            subtitle={`${((stats?.staked_supply / stats?.circulating_supply) * 100).toFixed(1)}% staked`}
            icon={Database}
            accentColor="success"
          />
          <StatCard
            title="Active Validators"
            value={formatNumber(stats?.validators_count, 0)}
            icon={Users}
            accentColor="secondary"
          />
          <StatCard
            title="Active Miners"
            value={formatNumber(stats?.miners_count, 0)}
            icon={Cpu}
            accentColor="warning"
          />
        </div>

        {/* Epoch Progress */}
        <div className="data-card p-6 mb-8" data-testid="epoch-progress">
          <h2 className="text-lg font-bold mb-4">Current Epoch Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-muted-foreground">Epoch {epochInfo?.epoch} / 10</span>
                <span className="text-sm font-mono">{epochInfo?.progress}%</span>
              </div>
              <Progress value={parseFloat(epochInfo?.progress || 0)} className="h-2 mb-4" />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Blocks in Epoch</p>
                  <p className="font-mono">{formatNumber(epochInfo?.blocksInEpoch, 0)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Blocks Remaining</p>
                  <p className="font-mono">{formatNumber(epochInfo?.blocksRemaining, 0)}</p>
                </div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-muted-foreground">Supply Mined</span>
                <span className="text-sm font-mono">{supplyMined.toFixed(2)}%</span>
              </div>
              <Progress value={supplyMined} className="h-2 mb-4" />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Mined</p>
                  <p className="font-mono">{formatNumber(stats?.circulating_supply - 1200000, 0)} GG</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Remaining</p>
                  <p className="font-mono">{formatNumber(19800000 - (stats?.circulating_supply - 1200000), 0)} GG</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tokenomics */}
        {tokenomics && (
          <div className="data-card p-6 mb-8" data-testid="tokenomics">
            <h2 className="text-lg font-bold mb-4">Tokenomics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Max Supply</p>
                <p className="text-xl font-bold font-mono">{formatNumber(tokenomics.max_supply, 0)} GG</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Genesis Supply</p>
                <p className="text-xl font-bold font-mono">{formatNumber(tokenomics.genesis_supply, 0)} GG</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Mineable</p>
                <p className="text-xl font-bold font-mono">{formatNumber(tokenomics.mineable_supply, 0)} GG</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Mining Duration</p>
                <p className="text-xl font-bold font-mono">{tokenomics.mining_duration_years} Years</p>
              </div>
            </div>
            
            <div className="flex items-center gap-8 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#00F0FF]"></div>
                <span className="text-sm">PoW: {(tokenomics.pow_ratio * 100).toFixed(0)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#7000FF]"></div>
                <span className="text-sm">PoS: {(tokenomics.pos_ratio * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Epoch Rewards Table */}
        {tokenomics && (
          <div className="data-card overflow-hidden" data-testid="epoch-rewards">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-lg font-bold">40-Year Mining Schedule</h2>
              <p className="text-sm text-muted-foreground">Halving every 4 years</p>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th>Epoch</th>
                    <th>Years</th>
                    <th>Total GG</th>
                    <th>Reward/Block</th>
                    <th>PoW (70%)</th>
                    <th>PoS (30%)</th>
                  </tr>
                </thead>
                <tbody>
                  {tokenomics.epoch_rewards.map((epoch, i) => (
                    <tr 
                      key={i} 
                      className={stats?.current_epoch === epoch.epoch ? 'bg-primary/10' : ''}
                    >
                      <td className="font-mono font-bold">
                        {epoch.epoch}
                        {stats?.current_epoch === epoch.epoch && (
                          <span className="ml-2 text-xs text-primary">(Current)</span>
                        )}
                      </td>
                      <td className="font-mono">{epoch.years}</td>
                      <td className="font-mono">{formatNumber(epoch.total_gg, 0)}</td>
                      <td className="font-mono text-primary">{epoch.reward_per_block.toFixed(6)}</td>
                      <td className="font-mono text-[#00F0FF]">{epoch.pow.toFixed(6)}</td>
                      <td className="font-mono text-[#7000FF]">{epoch.pos.toFixed(6)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
